import { PropsWithChildren, createContext, useCallback, useEffect, useMemo, useState } from 'react';

import { useQuery } from '@tanstack/react-query';

import {
  UserContext,
  clearUserContext,
  setUserContext,
} from '@//infra/observability/sentry/utils/user-context';
import { queryKeys } from '@/app/constants/queryKeys';
import { storageKeys } from '@/app/constants/storageKeys';
import { redirectToLogout } from '@/app/utils/auth/redirect';
import { LaunchScreen } from '@/common/components/modules/launch-screen';
import { storage } from '@/infra/protocols/storage';
import { IUser } from '@/modules/auth/entities/user.entity';
import { authService } from '@/modules/auth/services';

interface AuthContextValue {
  signedIn: boolean;
  signout: VoidFunction;
  user: IUser | undefined;
  signin(accessToken: string, refreshToken: string): void;
}

const AuthContext = createContext({} as AuthContextValue);

function mapUserToSentryContext(user: IUser): UserContext {
  return {
    id: user.id.toString(),
    email: user.email,
    name: user.fullName,
  };
}

function getAccessToken() {
  const storedAccessToken = storage.get(storageKeys.ACCESS_TOKEN);
  return storedAccessToken;
}

const AuthProvider = ({ children }: PropsWithChildren) => {
  const [signedIn, setSignedIn] = useState<boolean>(() => {
    const storedAccessToken = getAccessToken();
    return Boolean(storedAccessToken);
  });

  const { data, isSuccess, isFetching } = useQuery({
    gcTime: Infinity,
    enabled: signedIn,
    staleTime: Infinity,
    queryKey: queryKeys.users.me(),
    queryFn: () => authService.me(getAccessToken()),
  });

  const signin = useCallback((accessToken: string, refreshToken: string) => {
    storage.add(storageKeys.ACCESS_TOKEN, accessToken);
    storage.add(storageKeys.REFRESH_TOKEN, refreshToken);

    setSignedIn(true);
  }, []);

  const signout = useCallback(() => {
    storage.remove(storageKeys.ACCESS_TOKEN);
    storage.remove(storageKeys.REFRESH_TOKEN);
    setSignedIn(false);

    clearUserContext();
    redirectToLogout();
  }, []);

  const value = useMemo(
    () => ({ user: data, signin, signout, signedIn: isSuccess }),
    [data, isSuccess, signin, signout],
  );

  useEffect(() => {
    if (data && isSuccess) {
      setUserContext(mapUserToSentryContext(data));
    }
  }, [data, isSuccess]);

  return (
    <AuthContext.Provider value={value}>
      <LaunchScreen isLoading={isFetching} />
      {!isFetching && children}
    </AuthContext.Provider>
  );
};

export { AuthContext, AuthProvider, type AuthContextValue };
